import { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Phone, Mail, FileDown, Search, Inbox, Sparkles, Briefcase, CalendarClock, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { getFollowupStatus, sortByFollowup } from "@/lib/leadUtils";
import { PageHeader } from "@/pages/Portal";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";

const TYPE_TABS = [
  { v: "all", label: "All" },
  { v: "contact", label: "Contact" },
  { v: "quote", label: "Quote" },
  { v: "site_visit", label: "Site Visit" },
  { v: "career", label: "Careers" },
];
const TYPE_LABEL = { contact: "Contact", quote: "Quote", site_visit: "Site Visit", career: "Career" };
const LEAD_STATUS_FLOW = ["new", "contacted", "site_visit", "quotation", "negotiation", "won", "lost"];
const CAREER_STATUS_FLOW = ["new", "reviewed", "shortlisted", "interview", "selected", "rejected"];
const STATUS_COLOR = {
  new: "bg-amber-100 text-amber-700", contacted: "bg-blue-100 text-blue-700", site_visit: "bg-indigo-100 text-indigo-700",
  quotation: "bg-purple-100 text-purple-700", negotiation: "bg-orange-100 text-orange-700", won: "bg-green-100 text-green-700",
  lost: "bg-slate-200 text-slate-600", reviewed: "bg-blue-100 text-blue-700", shortlisted: "bg-indigo-100 text-indigo-700",
  interview: "bg-purple-100 text-purple-700", selected: "bg-green-100 text-green-700", rejected: "bg-slate-200 text-slate-600",
};
const FOLLOWUP_TABS = [
  { v: "all", label: "All" }, { v: "overdue", label: "Overdue" }, { v: "due_today", label: "Due Today" },
  { v: "upcoming", label: "Upcoming" }, { v: "none", label: "No Follow-Up Set" },
];
const SERVICE_AREA_OPTIONS = ["West Tripura", "Sepahijala", "Gomati", "South Tripura", "Khowai", "Dhalai", "Unakoti", "North Tripura"];
const FOLLOWUP_ROW_BG = { overdue: "bg-red-50/70", due_today: "bg-amber-50/70" };
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—";
const waLinkFor = (phone) => {
  const digits = (phone || "").replace(/\D/g, "");
  const full = digits.length === 10 ? `91${digits}` : digits;
  return `https://wa.me/${full}?text=${encodeURIComponent("Hello, this is SecureFlame Fire Systems following up on your enquiry.")}`;
};

function FollowupBadge({ lead }) {
  const status = getFollowupStatus(lead);
  if (status === "none") return <span className="text-xs text-muted-foreground">No follow-up set</span>;
  const due = new Date(lead.follow_up_date); due.setHours(0, 0, 0, 0);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const dateLabel = due.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  if (status === "overdue") {
    const days = Math.round((today.getTime() - due.getTime()) / 86400000);
    return <span data-testid={`followup-badge-${lead.id}`}><span className="block text-xs">{dateLabel}</span><Badge className="rounded-sm bg-red-100 text-red-700 mt-0.5">OVERDUE – {days} DAY{days === 1 ? "" : "S"}</Badge></span>;
  }
  if (status === "due_today") {
    return <span data-testid={`followup-badge-${lead.id}`}><span className="block text-xs">{dateLabel}</span><Badge className="rounded-sm bg-amber-100 text-amber-700 mt-0.5">DUE TODAY</Badge></span>;
  }
  return <span className="text-xs text-secondary" data-testid={`followup-badge-${lead.id}`}>{dateLabel}</span>;
}

export default function Leads() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [list, setList] = useState([]);
  const [tab, setTab] = useState("all");
  const [followupFilter, setFollowupFilter] = useState(searchParams.get("followup") || "all");
  const [areaFilter, setAreaFilter] = useState("all");
  const [sortMode, setSortMode] = useState("followup");
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [notes, setNotes] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(() => { api.get("/leads").then((r) => setList(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const fu = searchParams.get("followup");
    if (fu) setFollowupFilter(fu);
  }, [searchParams]);

  const filtered = useMemo(() => {
    let rows = tab === "all" ? list : list.filter((l) => l.type === tab);
    if (followupFilter !== "all") rows = rows.filter((l) => getFollowupStatus(l) === followupFilter);
    if (areaFilter !== "all") rows = rows.filter((l) => l.service_area === areaFilter);
    const q = search.trim().toLowerCase();
    if (q) rows = rows.filter((l) => [l.name, l.phone, l.email, l.organization, l.service_area].some((v) => (v || "").toLowerCase().includes(q)));
    if (sortMode === "followup") rows = sortByFollowup(rows);
    else if (sortMode === "newest") rows = [...rows].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    else if (sortMode === "oldest") rows = [...rows].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    return rows;
  }, [list, tab, followupFilter, areaFilter, search, sortMode]);

  const today = new Date().toDateString();
  const stats = {
    total: list.length,
    new: list.filter((l) => l.status === "new").length,
    siteVisits: list.filter((l) => l.type === "site_visit").length,
    quotes: list.filter((l) => l.type === "quote").length,
    careers: list.filter((l) => l.type === "career").length,
    todayNew: list.filter((l) => new Date(l.created_at).toDateString() === today).length,
  };

  const changeStatus = async (id, status) => {
    const fd = new FormData(); fd.append("status", status);
    try {
      await api.patch(`/leads/${id}/status`, fd);
      toast.success("Status updated");
      load();
      if (detail?.id === id) setDetail((d) => ({ ...d, status }));
    } catch (e) { toast.error("Could not update status"); }
  };

  const openDetail = (lead) => { setDetail(lead); setNotes(lead.notes || ""); setFollowUp(lead.follow_up_date || ""); setAssignedTo(lead.assigned_to || ""); };

  const saveNotes = async () => {
    if (!detail) return;
    setSaving(true);
    const fd = new FormData(); fd.append("notes", notes); fd.append("follow_up_date", followUp); fd.append("assigned_to", assignedTo);
    try {
      await api.patch(`/leads/${detail.id}/notes`, fd);
      toast.success("Notes saved");
      load();
    } catch (e) { toast.error("Could not save notes"); } finally { setSaving(false); }
  };

  const downloadCV = async (lead) => {
    try {
      const res = await api.get(`/leads/${lead.id}/cv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) { toast.error("Could not open CV"); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/leads/${deleteTarget.id}`);
      toast.success("Lead deleted");
      setDeleteTarget(null);
      if (detail?.id === deleteTarget.id) setDetail(null);
      load();
    } catch (e) { toast.error("Could not delete lead"); } finally { setDeleting(false); }
  };

  return (
    <div className="p-8">
      <PageHeader title="Leads" subtitle="Website Enquiries & Applications" />

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {[
          { label: "Total Leads", value: stats.total, icon: Inbox },
          { label: "New Leads", value: stats.new, icon: Sparkles },
          { label: "Today's New", value: stats.todayNew, icon: CalendarClock },
          { label: "Site Visits", value: stats.siteVisits, icon: Search },
          { label: "Quotations", value: stats.quotes, icon: FileDown },
          { label: "Careers", value: stats.careers, icon: Briefcase },
        ].map((c) => (
          <div key={c.label} className="bg-card border border-border rounded-sm p-4" data-testid={`lead-stat-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <c.icon size={16} className="text-primary mb-2" />
            <p className="text-2xl font-heading font-bold text-secondary">{c.value}</p>
            <p className="text-xs text-muted-foreground">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex flex-wrap gap-2" data-testid="leads-type-tabs">
          {TYPE_TABS.map((t) => (
            <button key={t.v} onClick={() => setTab(t.v)}
              className={`text-xs font-semibold px-3.5 py-1.5 rounded-full border transition-colors ${tab === t.v ? "bg-primary text-white border-primary" : "border-border text-secondary hover:border-primary/40"}`}
              data-testid={`leads-tab-${t.v}`}>{t.label}</button>
          ))}
        </div>
        <div className="relative w-full sm:w-64">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, phone, email…" className="pl-9 rounded-sm h-9 text-sm" data-testid="leads-search-input" />
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex flex-wrap gap-2" data-testid="leads-followup-tabs">
          {FOLLOWUP_TABS.map((t) => (
            <button key={t.v} onClick={() => { setFollowupFilter(t.v); setSearchParams(t.v === "all" ? {} : { followup: t.v }); }}
              className={`text-xs font-semibold px-3 py-1 rounded-full border transition-colors ${followupFilter === t.v ? "bg-secondary text-white border-secondary" : "border-border text-muted-foreground hover:border-secondary/40"}`}
              data-testid={`followup-tab-${t.v}`}>{t.label}</button>
          ))}
        </div>
        <Select value={sortMode} onValueChange={setSortMode}>
          <SelectTrigger className="w-44 h-8 rounded-sm text-xs" data-testid="leads-sort-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="followup">Sort: Next Follow-Up</SelectItem>
            <SelectItem value="newest">Sort: Newest</SelectItem>
            <SelectItem value="oldest">Sort: Oldest</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <Select value={areaFilter} onValueChange={setAreaFilter}>
          <SelectTrigger className="w-48 h-8 rounded-sm text-xs" data-testid="leads-area-select"><SelectValue placeholder="All Service Areas" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Service Areas</SelectItem>
            {SERVICE_AREA_OPTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-card border border-border rounded-sm overflow-hidden overflow-x-auto">
        <Table className="min-w-[1080px]">
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead>Date</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Service / Position</TableHead>
              <TableHead>Area</TableHead>
              <TableHead>Next Follow-Up</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((l) => (
              <TableRow key={l.id} data-testid={`lead-row-${l.id}`} className={FOLLOWUP_ROW_BG[getFollowupStatus(l)] || ""}>
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(l.created_at)}</TableCell>
                <TableCell><Badge variant="outline" className="rounded-sm">{TYPE_LABEL[l.type]}</Badge></TableCell>
                <TableCell className="font-medium text-secondary">{l.name}{l.organization && <div className="text-xs text-muted-foreground">{l.organization}</div>}</TableCell>
                <TableCell className="text-xs">
                  <a href={`tel:${l.phone}`} className="flex items-center gap-1 hover:text-primary"><Phone size={11} /> {l.phone}</a>
                  {l.email && <a href={`mailto:${l.email}`} className="flex items-center gap-1 hover:text-primary mt-0.5"><Mail size={11} /> {l.email}</a>}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-[12rem] truncate">{l.type === "career" ? l.position : (l.service || "—")}</TableCell>
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap" data-testid={`lead-area-${l.id}`}>{l.service_area || "—"}</TableCell>
                <TableCell><FollowupBadge lead={l} /></TableCell>
                <TableCell>
                  <Select value={l.status} onValueChange={(v) => changeStatus(l.id, v)}>
                    <SelectTrigger className="w-32 h-8 rounded-sm text-xs capitalize" data-testid={`lead-status-${l.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{(l.type === "career" ? CAREER_STATUS_FLOW : LEAD_STATUS_FLOW).map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-right space-x-1 whitespace-nowrap">
                  <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => openDetail(l)} data-testid={`lead-view-${l.id}`}>View</Button>
                  <a href={waLinkFor(l.phone)} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost" className="rounded-sm h-8 px-2" data-testid={`lead-whatsapp-${l.id}`}>WA</Button></a>
                  <Button size="sm" variant="ghost" className="rounded-sm h-8 px-2 text-primary hover:text-primary" onClick={() => setDeleteTarget(l)} data-testid={`lead-delete-${l.id}`}><Trash2 size={14} /></Button>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-12 text-muted-foreground">No leads found.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="rounded-sm max-w-lg max-h-[85vh] overflow-y-auto" data-testid="lead-detail-modal">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="text-secondary">{detail.name}</DialogTitle>
                <DialogDescription>{TYPE_LABEL[detail.type]} · {fmtDate(detail.created_at)} · Source: {detail.source || "—"}</DialogDescription>
              </DialogHeader>
              <div className="space-y-2 text-sm text-secondary">
                <p><b>Phone:</b> <a href={`tel:${detail.phone}`} className="text-primary">{detail.phone}</a></p>
                {detail.email && <p><b>Email:</b> <a href={`mailto:${detail.email}`} className="text-primary">{detail.email}</a></p>}
                {detail.organization && <p><b>Organization:</b> {detail.organization}</p>}
                {detail.building_type && <p><b>Building/Project type:</b> {detail.building_type}</p>}
                {detail.location && <p><b>Location:</b> {detail.location}</p>}
                {detail.service_area && <p><b>Service Area (District):</b> {detail.service_area}</p>}
                {detail.site_address && <p><b>Site address:</b> {detail.site_address}</p>}
                {detail.preferred_date && <p><b>Preferred date:</b> {detail.preferred_date}</p>}
                {detail.service && <p><b>Service required:</b> {detail.service}</p>}
                {detail.type === "career" && (
                  <>
                    <p><b>Position applied for:</b> {detail.position}</p>
                    {detail.qualification && <p><b>Qualification:</b> {detail.qualification}</p>}
                    {detail.experience && <p><b>Experience:</b> {detail.experience}</p>}
                    {detail.expected_salary && <p><b>Expected salary:</b> {detail.expected_salary}</p>}
                    {detail.cv_filename && (
                      <Button size="sm" variant="outline" className="rounded-sm gap-1.5 mt-1" onClick={() => downloadCV(detail)} data-testid="lead-download-cv-btn"><FileDown size={14} /> View / Download CV</Button>
                    )}
                  </>
                )}
                {detail.message && <p className="italic text-muted-foreground border-l-2 border-border pl-3">"{detail.message}"</p>}
              </div>
              <div className="border-t border-border pt-4 space-y-3">
                <div>
                  <Label htmlFor="lead-notes" className="text-xs uppercase tracking-wide text-muted-foreground">Internal Notes</Label>
                  <Textarea id="lead-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1.5 rounded-sm" data-testid="lead-notes-input" />
                </div>
                <div>
                  <Label htmlFor="lead-followup" className="text-xs uppercase tracking-wide text-muted-foreground">Next Follow-Up Date</Label>
                  <Input id="lead-followup" type="date" value={followUp} onChange={(e) => setFollowUp(e.target.value)} className="mt-1.5 rounded-sm w-48" data-testid="lead-followup-input" />
                </div>
                <div>
                  <Label htmlFor="lead-assigned" className="text-xs uppercase tracking-wide text-muted-foreground">Assigned To</Label>
                  <Input id="lead-assigned" value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} placeholder="Staff name" className="mt-1.5 rounded-sm w-48" data-testid="lead-assigned-input" />
                </div>
                <Button onClick={saveNotes} disabled={saving} size="sm" className="rounded-sm bg-primary hover:bg-primary/90" data-testid="lead-save-notes-btn">{saving ? "Saving…" : "Save"}</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-sm" data-testid="lead-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this lead?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently remove "{deleteTarget?.name}" from your Leads dashboard. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" data-testid="lead-delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} disabled={deleting} className="rounded-sm bg-primary hover:bg-primary/90" data-testid="lead-delete-confirm-btn">{deleting ? "Deleting…" : "Delete"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
